import { getUrlParameter } from '../../utils/js/getUrlParameter';

/**
 * TokyWoky module
 * @inheritDoc https://tokywoky.notion.site/Mise-en-place-d-une-plateforme-communautaire-3711dbee2522425b99d72c18795e43eb
 * @description
 * - User is Connected => setCookie => queryString contains "redirectToPage" => redirection
 * - User is notConnected => queryString contains "redirectToPage" => connexion/inscription => setCookie => redirection
 * @requires Connector
 * @requires getUrlParameter
 */
export class TokyWoky {
  #publicApiKey = 'df5c1e8e-8a57-444f-a039-afb1d170f2bd';
  #privateApiKey = '6ef367f1-cf00-4a37-a98e-9afea95e5bfe'; // not safe !
  #authMessage = null;
  #authHmac = '';
  #cookieName = 'toky_bep_sso';
  #baseUrl = '//communaute.centerparcs.fr'; // TokyWoky community domain
  authTimestamp = Math.floor(Date.now() / 1000); // seconds
  localStorageKey = 'users';

  constructor() {
    const isLoggedIn = !!this.getUserInfos();
    const hasRedirection = this.hasRedirection();

    // check if URL has redirection parameter (set by TokyWoky app)
    if (hasRedirection) {
      if (isLoggedIn) {
        // if user is logged-in and url has redirection parameter,
        // get user values, set values in a cookie and redirect to specified URL
        this.createCookieAndRedirect();
      } else {
        // if user is not logged-in, open connector panel
        Connector.open();
      }
    }

    // Listen events when Connector connected or disconnected
    window.addEventListener('storage', (e) => {
      if (e.key === this.localStorageKey) {
        const isLoggedIn = !!this.getUserInfos();
        const hasRedirection = this.hasRedirection();
        if (hasRedirection && isLoggedIn) {
          if (e.newValue !== e.oldValue) {
            this.createCookieAndRedirect();
          }
        }
      }
    });
  }

  /**
   * Get user infos
   * @return {object|boolean}
   */
  getUserInfos() {
    const userFromLocal = JSON.parse(localStorage.getItem(this.localStorageKey)) ?? null;
    return userFromLocal ? userFromLocal.user : false;
  }

  /**
   * Set AuthMessage (User infos)
   * @param {object} message - info from user LocalStorage
   */
  setAuthMessage(message) {
    this.#authMessage = btoa(JSON.stringify({
      id: message.uid,
      email: message.email,
      nickname: message.firstName + ' ' + message.lastName
    }));
  }

  /**
   * HMAC - SHA-256 encrypting
   * @param {string} key
   * @param {string} data
   * @return {Promise<{hex: string}>}
   * @constructor
   */
  async HMAC(key, data) {
    const encoder = new TextEncoder();
    const encodedKey = encoder.encode(key);
    const encodedData = encoder.encode(data);
    const hmacKey = await crypto.subtle.importKey('raw', encodedKey, {
      name: 'HMAC',
      hash: 'SHA-256'
    }, true, ['sign', 'verify']);
    const signature = await crypto.subtle.sign('HMAC', hmacKey, encodedData);
    const hex = Array.from(new Uint8Array(signature))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
    return {hex};
  }

  /**
   * Get hashed datas
   * @return {Promise<string>}
   */
  async getHash() {
    const message = `${this.#authMessage} ${this.authTimestamp}`;
    this.#authHmac = await this.HMAC(this.#privateApiKey, message).then(({hex}) => hex.toUpperCase());
    return this.#authHmac;
  }

  /**
   * Set cookie values
   * @requires cookieManagement
   */
  setCookie() {
    const toky_bep_sso = `${this.#publicApiKey}:${this.#authMessage}:${this.#authHmac}:${this.authTimestamp}`;
    const oneHourToDay = .0416667; // 1day/24h => 1 hour = 0.0416667 day

    cookieManagement.createCookie(this.#cookieName, toky_bep_sso, oneHourToDay, true);
  }

  hasRedirection() {
    const urlData = getUrlParameter('redirectToPage');
    return !!(urlData && urlData.length);
  }

  redirectToPage() {
    window.location = this.#baseUrl + '' + getUrlParameter('redirectToPage');
  }

  createCookieAndRedirect() {
    const userInfos = this.getUserInfos();
    if (userInfos) {
      this.setAuthMessage(userInfos);
      this.getHash().then(() => {
        this.setCookie();
        this.redirectToPage();
      });
    }
  }
}